import { useState, useEffect } from 'react'
import { Percent, X, Check, ArrowRight } from 'lucide-react'
import type { BookingRecord } from '../../../types'

export type VatRate = 10 | 20

type VatSelectionModalProps = {
  isOpen: boolean
  onClose: () => void
  booking: BookingRecord | null
  initialVat?: VatRate
  onConfirm: (vatRate: VatRate) => void
}

export const VatSelectionModal = ({
  isOpen,
  onClose,
  booking,
  initialVat = 10,
  onConfirm,
}: VatSelectionModalProps) => {
  const [selectedVat, setSelectedVat] = useState<VatRate>(initialVat)

  useEffect(() => {
    if (isOpen) {
      setSelectedVat(initialVat)
    }
  }, [isOpen, initialVat])

  if (!isOpen || !booking) return null

  const handleProceed = () => {
    onConfirm(selectedVat)
  }

  return (
    <div className="fixed inset-0 z-[115] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-[480px] rounded-[20px] bg-white shadow-2xl overflow-hidden border border-gray-100 font-host animate-in zoom-in-95 duration-200">

        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FF6A1A]/10 text-[#FF6A1A]">
              <Percent size={20} strokeWidth={2.5} />
            </div>
            <div>
              <h3 className="text-[17px] font-bold text-gray-900 leading-tight">
                Select VAT Option
              </h3>
              <p className="text-[12px] text-gray-500 mt-0.5">
                Booking #{booking.reference} • {booking.customer.name}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-4">
          <p className="text-[13px] text-gray-600 leading-relaxed">
            Choose the statutory VAT rate to display on the invoice. Prices shown on the invoice will indicate that this tax percentage is included.
          </p>

          {/* VAT Options Radio Cards */}
          <div className="grid grid-cols-1 gap-3 pt-1">

            {/* 10% Option */}
            <div
              onClick={() => setSelectedVat(10)}
              className={`group relative flex items-start gap-4 p-4 rounded-[14px] border-2 cursor-pointer transition-all duration-200 ${
                selectedVat === 10
                  ? 'border-[#FF6A1A] bg-[#FFF8F5] shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full mt-0.5 border transition-all ${
                selectedVat === 10
                  ? 'border-[#FF6A1A] bg-[#FF6A1A] text-white'
                  : 'border-gray-300 group-hover:border-gray-400 bg-white'
              }`}>
                {selectedVat === 10 && <Check size={12} strokeWidth={3} />}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-[16px] font-bold text-gray-900">
                    10% VAT
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Reduced Rate
                  </span>
                </div>
                <p className="text-[12px] text-gray-500 mt-1 leading-normal">
                  Standard passenger transport, day trips, and local travel services in Austria.
                </p>
              </div>
            </div>

            {/* 20% Option */}
            <div
              onClick={() => setSelectedVat(20)}
              className={`group relative flex items-start gap-4 p-4 rounded-[14px] border-2 cursor-pointer transition-all duration-200 ${
                selectedVat === 20
                  ? 'border-[#FF6A1A] bg-[#FFF8F5] shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full mt-0.5 border transition-all ${
                selectedVat === 20
                  ? 'border-[#FF6A1A] bg-[#FF6A1A] text-white'
                  : 'border-gray-300 group-hover:border-gray-400 bg-white'
              }`}>
                {selectedVat === 20 && <Check size={12} strokeWidth={3} />}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-[16px] font-bold text-gray-900">
                    20% VAT
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                    Standard Rate
                  </span>
                </div>
                <p className="text-[12px] text-gray-500 mt-1 leading-normal">
                  General standard Austrian rate for commercial bookings and business services.
                </p>
              </div>
            </div>

          </div>

          <div className="rounded-[10px] bg-gray-50 p-3 border border-gray-100 text-[12px] text-gray-500 flex items-center justify-between">
            <span>Selected on Invoice:</span>
            <span className="font-bold text-gray-900 font-mono">
              {selectedVat}% VAT Included
            </span>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-200/60 transition-colors"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleProceed}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#FF6A1A] hover:bg-[#E05B13] active:scale-95 text-white text-[13px] font-bold shadow-sm transition-all"
          >
            <span>Preview Invoice</span>
            <ArrowRight size={15} />
          </button>
        </div>

      </div>
    </div>
  )
}
